import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import healthcareBg from "@/assets/home-hero/hospitality.webp";
import slide3Bg from "@/assets/home-hero/slide-3.webp";

// Slide 1 is served from /public (not webpack-processed) at a stable path,
// matching the <link rel="preload"> tags in public/index.html -- that lets
// the browser start fetching this LCP image straight from the static HTML,
// instead of waiting for the JS bundle to load and React to render it.
// Two crops: a portrait one (text laid out for a tall phone screen) and the
// original wide one for desktop -- swapped at the "md" breakpoint below,
// matching the media queries on the preload links.
const defaultHeroBg = `${process.env.PUBLIC_URL}/hero/hero-default.webp`;
const defaultHeroBgMobile = `${process.env.PUBLIC_URL}/hero/hero-default-mobile.webp`;

// Simple static-image carousel -- no hover interactivity, no grid dividers.
// Slide 1 is the branded on-load photo (own headline baked into the image).
// Slides 2-3 are plain site photos, so the site's own H1 overlays them.
const HERO_SLIDES = [
  {
    id: "brand",
    image: defaultHeroBg,
    mobileImage: defaultHeroBgMobile,
    showHeadline: false,
  },
  { id: "healthcare", image: healthcareBg, showHeadline: true },
  { id: "slide-3", image: slide3Bg, showHeadline: true },
];
const HERO_SLIDE_DURATION = 8000;

export default function HeroGallery() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((i) => (i + 1) % HERO_SLIDES.length);
    }, HERO_SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [active, paused]);

  const slide = HERO_SLIDES[active];
  const goPrev = () =>
    setActive((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const goNext = () => setActive((i) => (i + 1) % HERO_SLIDES.length);

  return (
    <section
      data-testid="hero-gallery"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      className="relative h-[100svh] w-full overflow-hidden bg-[#06131F] text-white"
    >
      {/* Background carousel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
          className="absolute inset-0"
          data-testid="hero-slide-bg"
        >
          {slide.mobileImage ? (
            <>
              <div
                className="absolute inset-0 bg-contain bg-no-repeat bg-center md:hidden"
                style={{ backgroundImage: `url('${slide.mobileImage}')` }}
              />
              <div
                className="absolute inset-0 bg-cover bg-center hidden md:block"
                style={{ backgroundImage: `url('${slide.image}')` }}
              />
            </>
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${slide.image}')` }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Prev/next arrows */}
      <button
        type="button"
        onClick={goPrev}
        aria-label="Previous slide"
        data-testid="hero-prev"
        className="absolute left-3 md:left-6 top-[64%] -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/25 hover:bg-black/45 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white transition-colors duration-300"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="Next slide"
        data-testid="hero-next"
        className="absolute right-3 md:right-6 top-[64%] -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/25 hover:bg-black/45 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white transition-colors duration-300"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {/* Subtle overlay for text legibility -- kept light so image stays vivid */}
      <div className="absolute inset-0 bg-[#06131F]/15 pointer-events-none" />
      {/* Vignette -- only edges, keeps center bright */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#06131F]/25 via-transparent to-[#06131F]/55 pointer-events-none" />
      {/* Scrim behind the headline -- only shown on slides that need the
          site's own H1 legible over the photo. */}
      {/* <div
        className="absolute left-0 bottom-0 w-full sm:w-[75%] md:w-[55%] h-[55%] bg-gradient-to-t from-[#06131F]/25 via-[#06131F]/45 to-transparent pointer-events-none transition-opacity duration-300"
        style={{ opacity: slide.showHeadline ? 1 : 0 }}
      /> */}

      {/* Foreground title -- floats over everything */}
      <div className="relative z-20 h-full flex flex-col justify-end pointer-events-none">
        <div className="px-6 md:px-10 max-w-[1400px] mx-auto w-full pointer-events-auto">
          <motion.h1
            initial={false}
            animate={{
              opacity: slide.showHeadline ? 1 : 0,
              y: slide.showHeadline ? 0 : 24,
            }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif text-4xl sm:text-5xl md:text-7xl leading-[1.05] max-w-4xl text-white drop-shadow-[0_6px_28px_rgba(0,0,0,0.8)] pointer-events-none"
          >
            India's{" "}
            <span className="italic text-white">
              First Antimicrobial Cladding Installation
            </span>{" "}
            Company
          </motion.h1>
        </div>

        <div className="pb-6 md:pb-8 mt-8 md:mt-10 px-6 md:px-10 max-w-[1400px] mx-auto w-full flex flex-col md:flex-row md:items-end md:justify-between gap-4 pointer-events-auto">
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="hero-gallery-cta"
              className="btn-primary"
            >
              Start a project <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/clients"
              className="btn-ghost !text-white !border-white/30 hover:!bg-white hover:!text-[#0F2A44]"
            >
              All sectors
            </Link>
          </div>
        </div>

        {/* Carousel indicators -- active dot fills over the dwell time */}
        <div
          data-testid="hero-carousel-dots"
          className="pb-6 md:pb-8 px-6 md:px-10 max-w-[1400px] mx-auto w-full flex items-center gap-3 pointer-events-auto"
        >
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className="relative w-10 md:w-12 h-1.5 rounded-full bg-white/25 overflow-hidden"
            >
              {active === i && (
                <motion.div
                  key={active}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{
                    duration: HERO_SLIDE_DURATION / 1000,
                    ease: "linear",
                  }}
                  className="absolute inset-0 bg-[#189A96] origin-left"
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
